import React from "react";

import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

type ChatErrorBoundaryProps = {
	children: React.ReactNode;
};

type ChatErrorBoundaryState = {
	hasError: boolean;
	error: Error | null;
};

/** Error boundary that displays fallback UI instead of crashing the app. */
export class ChatErrorBoundary extends React.Component<ChatErrorBoundaryProps, ChatErrorBoundaryState> {
	constructor(props: ChatErrorBoundaryProps) {
		super(props);
		this.state = { hasError: false, error: null };
	}

	static getDerivedStateFromError(error: Error): ChatErrorBoundaryState {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
		// Log error to monitoring service in production
		console.error("AskPerformYard error:", error, errorInfo);
	}

	handleReset = (): void => {
		this.setState({ hasError: false, error: null });
	};

	render(): React.ReactNode {
		if (this.state.hasError) {
			return (
				<div className="fixed bottom-24 right-6 z-[1200] w-[416px] rounded-lg bg-white shadow-[0_4px_30px_0_rgba(0,0,0,0.15)]">
					{/* Header */}
					<div className="flex items-center justify-between border-b border-neutral-200 px-4 py-3">
						<div className="flex items-center gap-2">
							<img src="/static/img/py-logo/py-logo-solo.png" alt="PerformYard" className="h-6 w-6" />
							<span className="text-sm font-semibold text-neutral-600">Ask PerformYard</span>
						</div>
					</div>

					{/* Error content */}
					<div className="p-4">
						<div className="flex gap-3 rounded-lg border border-red-200 bg-red-50 p-4">
							<FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 text-red-600" />
							<div className="flex-1">
								<p className="mb-1 text-sm font-semibold text-red-800">Something went wrong</p>
								<p className="text-xs leading-snug text-red-700">
									The chat interface encountered an unexpected error. Please try again.
								</p>
								<button
									onClick={this.handleReset}
									className="mt-2 rounded border border-red-200 bg-white px-3 py-1.5 text-xs font-medium text-red-700 transition-colors hover:bg-red-50"
								>
									Try again
								</button>
							</div>
						</div>
					</div>
				</div>
			);
		}

		return this.props.children;
	}
}

export default ChatErrorBoundary;
